// Semua pesan WhatsApp ke customer disusun DI SINI saja (31 Okt 2026).
// Dulu ada di 4 tempat dengan isi berbeda-beda dan tombol di Order Sewa selalu
// mengirim "order sudah kami catat" walaupun barang sudah kembali.
// Gaya yang dipilih Deddy: hangat, sapaan "Kak", emoji sedikit.
import { formatIDR, formatDateLong, formatSizes, todayStr, shiftDateStr } from './format';

// ---------- nomor ----------
// Mengubah isi kolom WhatsApp jadi nomor untuk wa.me.
//   0812.. / +62 812.. / 62 0812.. / 812..  -> 62812..
//   "0812.. / 0813.." (dua nomor)           -> nomor pertama, status 'beberapa'
//   nomor luar negeri yang diawali +       -> dipakai apa adanya
export function nomorWa(raw) {
  const asli = String(raw || '').trim();
  if (!asli) return { nomor: '', status: 'kosong', asli };
  const bagian = asli.split(/\s*(?:\/|,|;|\||\n|\bdan\b|\batau\b)\s*/i).filter((x) => /\d/.test(x));
  const pertama = (bagian[0] || '').trim();
  let d = pertama.replace(/\D/g, '');
  const luar = pertama.startsWith('+') && !d.startsWith('62');
  if (!luar) {
    if (d.startsWith('620')) d = '62' + d.slice(3);
    else if (d.startsWith('0')) d = '62' + d.slice(1);
    else if (d.startsWith('8')) d = '62' + d;
  }
  const valid = luar ? /^\d{8,15}$/.test(d) : /^628\d{7,12}$/.test(d);
  if (!valid) return { nomor: '', status: 'salah', asli };
  return { nomor: d, status: bagian.length > 1 ? 'beberapa' : 'ok', asli };
}

export const tampilNomor = (n) => {
  if (!n) return '-';
  if (!n.startsWith('62')) return '+' + n;
  const lokal = '0' + n.slice(2);
  return lokal.replace(/^(\d{4})(\d{4})(\d+)$/, '$1-$2-$3');
};

export const linkWa = (nomor, teks) =>
  `https://wa.me/${nomor || ''}${teks ? `?text=${encodeURIComponent(teks)}` : ''}`;

// Nama di aplikasi kadang memuat catatan internal ("Ana/pacar iwan", "Rusna (2)").
// Yang dipakai menyapa hanya bagian depannya; teks tetap bisa diubah sebelum dikirim.
export function namaSapaan(nama) {
  return String(nama || '').split(/[/(|]|\s-\s/)[0].trim();
}

// ---------- potongan ----------
const usahaDari = (s) => (s?.business_name || '').trim() || 'Deyran Gallery';

function hariRelatif(tgl, hari) {
  if (!tgl) return '';
  if (tgl === hari) return `hari ini, ${formatDateLong(tgl)}`;
  if (tgl === shiftDateStr(hari, 1)) return `besok, ${formatDateLong(tgl)}`;
  return formatDateLong(tgl);
}

function barisBarang(d) {
  return (d.rows || []).map((r) => {
    const uk = formatSizes(r.sizes || {});
    const bw = r.bawahan_name ? ` + ${r.bawahan_name}` : '';
    return `• ${r.product_name} (${r.qty} pcs${uk ? `, ${uk}` : ''})${bw}`;
  });
}

function namaAksesoris(d) {
  const set = new Set();
  (d.rows || []).forEach((r) => (r.aksesoris || []).forEach((a) => a?.nama && set.add(a.nama.toLowerCase())));
  const a = [...set];
  if (a.length <= 1) return a.join('');
  return `${a.slice(0, -1).join(', ')} dan ${a[a.length - 1]}`;
}

function jaminanTeks(d) {
  const t = d.ktp_type || (d.ktp ? 'titip_ktp' : 'tidak_ada');
  if (t === 'titip_ktp') return d.ktp_name || d.ktp ? `Titip KTP (a.n. ${d.ktp_name || d.ktp})` : 'Titip KTP';
  if (t === 'kirim_wa') return 'Foto KTP via WhatsApp';
  return '';
}

const nomorOrder = (d) => d.transaction_number || d.booking_number || '';

function dendaBelum(d) {
  const x = d.denda;
  if (!x || x.status !== 'belum' || !(Number(x.nominal) > 0)) return null;
  return { nominal: Number(x.nominal), jenis: (x.jenis || 'denda').toLowerCase() };
}

// ---------- jenis pesan ----------
export const JENIS_PESAN = {
  konfirmasi: 'Konfirmasi order',
  ingat_ambil: 'Pengingat ambil',
  ingat_kembali: 'Pengingat kembali',
  telat: 'Telat kembali',
  terima_sebagian: 'Terima sebagian',
  terima_kasih: 'Terima kasih',
  batal: 'Pembatalan',
  sapa: 'Sapaan',
};

// Pilihan yang masuk akal untuk status order/booking saat ini.
export function daftarJenis(d) {
  if (!d) return ['sapa'];
  if (d.status === 'cancelled') return ['batal', 'sapa'];
  if (d.status === 'completed') return ['terima_kasih', 'konfirmasi', 'sapa'];
  if ((d.sisa_kembali || []).length) return ['terima_sebagian', 'ingat_kembali', 'telat', 'sapa'];
  return ['konfirmasi', 'ingat_ambil', 'ingat_kembali', 'telat', 'sapa'];
}

// Pesan yang langsung dipilih saat tombol WA ditekan.
export function jenisBawaan(d, hari = todayStr()) {
  if (!d) return 'sapa';
  if (d.status === 'cancelled') return 'batal';
  if (d.status === 'completed') return 'terima_kasih';
  if ((d.sisa_kembali || []).length) return 'terima_sebagian';
  const belumAmbil = !d.picked_up && (d.pickup_date || '') >= hari;
  if (belumAmbil) return d.pickup_date <= shiftDateStr(hari, 1) ? 'ingat_ambil' : 'konfirmasi';
  if (d.return_date && d.return_date < hari) return 'telat';
  return 'ingat_kembali';
}

// ---------- isi pesan ----------
export function susunPesan(jenis, d, settings = {}, hari = todayStr()) {
  const nama = namaSapaan(d?.customer_name);
  const usaha = usahaDari(settings);
  const sapa = nama ? `Halo Kak ${nama} 😊` : 'Halo Kak 😊';
  if (jenis === 'sapa' || !d) return `${sapa}\nKami dari ${usaha}. `;

  const no = nomorOrder(d);
  const isOrder = !!d.transaction_number;
  const sisa = isOrder ? Number(d.remaining || 0) : 0;
  const L = [];

  if (jenis === 'konfirmasi') {
    L.push(sapa, `Terima kasih sudah mempercayakan busana untuk acara Kakak kepada ${usaha}. Order Kakak sudah kami catat dengan rincian berikut:`, '');
    if (no) L.push(`*No. Order:* ${no}`);
    L.push('*Barang:*', ...barisBarang(d));
    L.push(`*Ambil:* ${formatDateLong(d.pickup_date)}`, `*Pakai:* ${formatDateLong(d.use_date)}`, `*Kembali:* ${formatDateLong(d.return_date)}`);
    if (isOrder) {
      L.push('', `*Total:* ${formatIDR(d.total)}`, `*Sudah dibayar:* ${formatIDR(d.paid)}`);
      L.push(sisa > 0 ? `*Sisa:* ${formatIDR(sisa)}` : '*Status:* LUNAS');
      const j = jaminanTeks(d);
      if (j) L.push(`*Jaminan:* ${j}`);
    }
    L.push('', 'Kalau ada yang ingin ditanyakan atau diubah, silakan balas pesan ini ya, Kak. Sampai jumpa di hari pengambilan 🤍');
  } else if (jenis === 'ingat_ambil') {
    L.push(sapa, `Kami mengingatkan jadwal pengambilan busana Kakak: *${hariRelatif(d.pickup_date, hari)}*.`, '');
    if (no) L.push(`*No. Order:* ${no}`);
    L.push('*Barang:*', ...barisBarang(d), `*Pakai:* ${formatDateLong(d.use_date)}`, `*Kembali:* ${formatDateLong(d.return_date)}`);
    const tambahan = [];
    if (sisa > 0) tambahan.push(`Sisa pembayaran *${formatIDR(sisa)}* bisa dilunasi saat pengambilan.`);
    if ((d.ktp_type || (d.ktp ? 'titip_ktp' : '')) === 'titip_ktp') tambahan.push('Jangan lupa membawa KTP untuk jaminan ya, Kak.');
    if (tambahan.length) L.push('', ...tambahan);
    L.push('', `Sampai jumpa di ${usaha} 🤍`);
  } else if (jenis === 'ingat_kembali') {
    L.push(sapa, `Semoga acaranya lancar dan Kakak tampil cantik dengan busana dari ${usaha} ✨`,
      `Sekadar mengingatkan, jadwal pengembalian busana Kakak *${hariRelatif(d.return_date, hari)}*.`, '');
    if (no) L.push(`*No. Order:* ${no}`);
    L.push('*Barang:*', ...barisBarang(d));
    if (sisa > 0) L.push('', `Sisa pembayaran *${formatIDR(sisa)}* bisa dilunasi saat pengembalian.`);
    const aks = namaAksesoris(d);
    L.push('', `Mohon dicek kelengkapannya${aks ? ` (termasuk ${aks})` : ''} sebelum dikembalikan ya, Kak. Terima kasih 🤍`);
  } else if (jenis === 'telat') {
    const lewat = d.return_date ? Math.max(1, Math.round((new Date(hari) - new Date(d.return_date)) / 86400000)) : 0;
    L.push(sapa, `Kami ingin mengingatkan, busana${no ? ` order ${no}` : ''} seharusnya dikembalikan pada *${formatDateLong(d.return_date)}*${lewat ? ` (sudah lewat ${lewat} hari)` : ''}.`, '');
    L.push('*Barang:*', ...barisBarang(d), '');
    L.push('Mohon dikembalikan secepatnya ya, Kak, agar busananya bisa segera kami siapkan untuk penyewa berikutnya. Keterlambatan pengembalian dapat dikenakan biaya keterlambatan sesuai ketentuan.');
    L.push('', 'Kalau ada kendala, silakan kabari kami supaya bisa dicarikan jalan keluarnya. Terima kasih atas pengertiannya 🙏');
  } else if (jenis === 'terima_sebagian') {
    L.push(sapa, `Busana${no ? ` order ${no}` : ''} sudah kami terima kembali sebagian. Terima kasih, Kak 🤍`, '',
      'Yang masih belum kami terima:');
    (d.sisa_kembali || []).forEach((x) => L.push(`• ${x.product_name}${x.size ? ` (${x.size})` : ''} — ${x.qty} pcs`));
    L.push('', 'Mohon dilengkapi secepatnya ya, Kak. Kalau ada kendala, kabari kami supaya bisa kami bantu carikan jalan keluarnya. Terima kasih atas pengertiannya 🙏');
  } else if (jenis === 'terima_kasih') {
    L.push(sapa, `Busana${no ? ` order ${no}` : ''} sudah kami terima kembali. Terima kasih banyak sudah memilih ${usaha} untuk menemani acara Kakak 🤍`);
    const dn = dendaBelum(d);
    if (sisa > 0 || dn) {
      const bagian = [];
      if (sisa > 0) bagian.push(`sisa pembayaran *${formatIDR(sisa)}*`);
      if (dn) bagian.push(`biaya ${dn.jenis} *${formatIDR(dn.nominal)}*`);
      L.push('', `Sebagai pengingat, masih ada ${bagian.join(' dan ')}. Bisa dilunasi lewat transfer atau langsung di toko ya, Kak.`);
    }
    const ig = (settings?.instagram || '').trim().replace(/^@/, '');
    const maps = (settings?.google_maps_url || '').trim();
    if (ig || maps) {
      L.push('', 'Kami senang sekali kalau Kakak berkenan:');
      if (ig) L.push(`• Membagikan foto saat memakai busana kami dan tag Instagram @${ig}`);
      if (maps) L.push(`• Memberi ulasan di Google Maps: ${maps}`);
    }
    L.push('', 'Kritik dan saran juga sangat kami tunggu, supaya pelayanan kami makin baik.');
    L.push('Kalau ada acara berikutnya, kami siap membantu lagi. Sampai jumpa, Kak!');
  } else if (jenis === 'batal') {
    L.push(sapa, `Kami konfirmasi, order${no ? ` ${no}` : ''} untuk tanggal pakai ${formatDateLong(d.use_date)} sudah kami batalkan.`);
    const kembali = Number(d.cancel_refunded || 0);
    const hangus = Number(d.cancel_forfeited || 0);
    if (kembali > 0) L.push(`Pengembalian dana sebesar *${formatIDR(kembali)}* sudah kami proses.`);
    if (hangus > 0) L.push(`Sesuai ketentuan, DP sebesar *${formatIDR(hangus)}* tidak dapat dikembalikan.`);
    L.push('', 'Terima kasih, Kak. Semoga lain waktu kami bisa melayani Kakak kembali 🤍');
  }
  return L.join('\n');
}
